/**
 * The planet behind the welcome card: a made-up world, big as a planet of level 8, that shows what
 * the game has, so a new player sees what the crew is working towards: green land with saplings,
 * forests, lakes, stone, and ore with crystal resonators at work on it. Flat, like the game while
 * heights are switched off. It is a picture, never a game state: the rules don't run on it.
 */
import { KIND, makeGrid, type Grid, type Kind } from '@bramvasterd/terrella-rules';

import { hash2 } from '@core/math';

import { glowOf } from '../models/machines';
import type { MachineView, PlanetView } from '../planet/view';
import { storyGroups } from '../planet/groups';

/**
 * Its size: 12 subdivisions, 1,442 tiles, light enough for an older phone. A size rather than a
 * level, so a bigger first planet (and so a bigger level 8) never makes it heavier. The end-to-end
 * tests set VITE_SHOWCASE_SIZE lower, as headless browsers render in software.
 */
export const SHOWCASE_SIZE = Number(import.meta.env.VITE_SHOWCASE_SIZE ?? 12) || 12;

/** Grows a patch outwards from `seed`, tile by tile, until it holds about `size` tiles. */
function patch(grid: Grid, seed: number, size: number, salt: number, taken: Uint8Array): number[] {
  const out: number[] = [];
  const queue = [seed];
  const seen = new Set<number>([seed]);
  while (queue.length > 0 && out.length < size) {
    // take a random-ish tile from the frontier, so the patch is blobby, not round
    const at = Math.floor(
      hash2(queue.length * 7 + (queue[0] ?? 0), salt) * Math.min(queue.length, 4),
    );
    const tile = queue.splice(at, 1)[0] ?? 0;
    if (taken[tile]) continue;
    out.push(tile);
    taken[tile] = 1;
    for (const next of grid.neighbors[tile] ?? []) {
      if (!seen.has(next) && !taken[next]) {
        seen.add(next);
        queue.push(next);
      }
    }
  }
  return out;
}

export function showcasePlanet(subdivisions = SHOWCASE_SIZE): PlanetView {
  const grid = makeGrid(subdivisions);
  const n = grid.count;
  const kind = new Uint8Array(n);
  const taken = new Uint8Array(n);
  const pick = (salt: number): number => Math.floor(hash2(n, salt) * n);
  /** Lay patches of one kind, each about `size` tiles; returns them. */
  const patches = (
    sizes: readonly (readonly [number, number])[],
    paint: (tile: number) => Kind,
  ): number[][] =>
    sizes.map(([salt, size]) => {
      const tiles = patch(grid, pick(salt), size, salt, taken);
      for (const tile of tiles) kind[tile] = paint(tile);
      return tiles;
    });

  // Stone, and ore for the resonators.
  patches(
    [
      [21, 14],
      [22, 10],
      [23, 7],
    ],
    () => KIND.STONE,
  );
  const ores = patches(
    [
      [61, 6],
      [62, 4],
    ],
    () => KIND.ORE,
  );
  // Lakes.
  patches(
    [
      [31, 40],
      [32, 22],
      [33, 14],
      [34, 9],
    ],
    () => KIND.WATER,
  );
  // Forests.
  patches(
    [
      [41, 48],
      [42, 36],
      [43, 28],
      [44, 20],
      [45, 14],
    ],
    () => KIND.TREE,
  );

  // Everything else: grass with saplings sprinkled, a few trees, a little soil and stone.
  for (let tile = 0; tile < n; tile++) {
    if (taken[tile]) continue;
    const r = hash2(tile, 51);
    if (r < 0.74) kind[tile] = KIND.GRASS;
    else if (r < 0.82) kind[tile] = KIND.SAPLING;
    else if (r < 0.88) kind[tile] = KIND.TREE;
    else if (r < 0.95) kind[tile] = KIND.SOIL;
    else kind[tile] = KIND.STONE;
  }
  // A crystal resonator in the middle of each ore patch, part full, the ore it reaches glowing.
  const machines = new Map<number, MachineView>();
  const drawn = new Uint8Array(n);
  ores.forEach((ore, k) => {
    const at = ore[0];
    if (at === undefined) return;
    const fill = 0.4 + 0.3 * k;
    machines.set(at, { type: 'crystal', level: 1, fillAt: () => fill, drawing: true });
    for (const t of [at, ...(grid.neighbors[at] ?? [])])
      if (kind[t] === KIND.ORE) drawn[t] = glowOf('crystal');
  });
  const elev = new Int8Array(n);
  const groups = storyGroups(grid, kind);
  return {
    grid,
    kind,
    elev,
    groups,
    machines,
    drawn,
  };
}
